/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import user from 'js/lib/user';

import { Link, Typography, Typography2, useTheme } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/program-common';

import SkillCardWithScore from './SkillCardWithScore';
import type { SkillSearchResults } from './SkillSearchDataProvider';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

type Props = {
  skillHits: SkillSearchResults[];
  programHomeLink: string;
  programSlug: string;
  currentRefinement: string | undefined;
};

const useStyles = () => {
  const { spacing, breakpoints } = useTheme();
  return {
    banner: css`
      ${breakpoints.down('sm')} {
        margin: ${spacing(12)};
      }
    `,
    header: css`
      display: flex;
      gap: ${spacing(12)};
      padding: ${spacing(0, 0, 8)};
      flex-wrap: wrap;
      align-items: end;
    `,
    // To fix alignment issues
    allSkillsLink: css`
      height: 24px;
    `,
    skillsContainer: css`
      padding-top: ${spacing(12)};
      padding-bottom: ${spacing(24)};
      display: flex;
      flex-wrap: wrap;
      gap: ${spacing(24)};
    `,
    skillCard: css`
      flex: 1 1 31%;
      max-width: 31%;
      ${breakpoints.down('sm')} {
        flex: 1 1 100%;
        max-width: 100%;
      }
    `,
  };
};

const SkillSearchResultBanner = ({ skillHits, programHomeLink, programSlug, currentRefinement }: Props) => {
  const styles = useStyles();

  const matches = skillHits[0].hits.slice(0, 3);

  const trackingData = {
    skillMatches: matches.map((match) => ({
      name: match.name,
      objectID: match.objectID,
    })),
  };

  return (
    <div css={styles.banner}>
      <div css={styles.header}>
        <Typography variant="h2semibold" component="h2">
          {_t('Find your next course through a Skill')}
        </Typography>
        <TrackedLink
          {...Link.defaultProps}
          css={styles.allSkillsLink}
          trackingName="search_banner_all_skills"
          trackingData={trackingData}
          href={`${programHomeLink}?currentTab=MY_LEARNING&myLearningTab=SKILLS_DASHBOARD`}
        >
          {_t('Explore all available Skills')}
        </TrackedLink>
      </div>
      <Typography2 component="p">{_t('Build deep proficiency with expertly matched recommendations.')}</Typography2>
      <div css={styles.skillsContainer}>
        {matches.map((skill, index) => (
          <SkillCardWithScore
            key={skill.objectID}
            userId={user.get().id}
            css={styles.skillCard}
            skillId={skill.objectID.split('~')[1]}
            skillName={skill.name}
            currentRefinement={currentRefinement}
            trackingData={{
              matches,
              skill,
              index,
              order: index,
              pageSection: {
                sectionName: 'cta_banner',
                index: 2,
              },
            }}
            programSlug={programSlug}
          />
        ))}
      </div>
    </div>
  );
};

export default SkillSearchResultBanner;
